import { useState, useEffect } from "react";
import api from "../../lib/api";
import {
  X,
  User,
  Bot,
  Calendar,
  Clock,
  Phone,
  Mail,
  ExternalLink,
  Maximize2,
  AlertCircle,
  RefreshCw,
  Image as ImageIcon,
  CheckCircle2,
} from "lucide-react";
import { cn } from "../../lib/utils";
import FormattedMessage, { isRichEmbed } from "./FormattedMessage";
import {
  OPS_STATUS_OPTIONS,
  getOpsStatusBadgeClass,
  getOpsStatusDotColor,
} from "./opsStatusHelpers";
import { getUserStatusBadge, getConvertedBadge } from "./conversionHelpers";
import ConversionDetailModal from "./ConversionDetailModal";

export default function ConversationHistoryModal({
  conversationId,
  initialData,
  onClose,
  onEvaluationUpdated,
}) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lightboxImg, setLightboxImg] = useState(null);
  const [showConversionModal, setShowConversionModal] = useState(false);

  // Evaluation states
  const [evalStatus, setEvalStatus] = useState(
    initialData?.evaluation?.status || initialData?.ops_status || "Pending"
  );
  const [evalRemarks, setEvalRemarks] = useState(
    initialData?.evaluation?.remarks || ""
  );
  const [savingEval, setSavingEval] = useState(false);
  const [evalFeedback, setEvalFeedback] = useState(null);

  useEffect(() => {
    if (!conversationId) return;

    let mounted = true;
    setLoading(true);
    setError(null);

    api
      .get(`/ai/chat-history/conversations/${conversationId}`)
      .then((res) => {
        if (mounted) {
          if (res.data?.success && res.data.conversation) {
            setData(res.data.conversation);
            if (res.data.conversation.evaluation) {
              setEvalStatus(res.data.conversation.evaluation.status || "Pending");
              setEvalRemarks(res.data.conversation.evaluation.remarks || "");
            } else if (res.data.conversation.ops_status) {
              setEvalStatus(res.data.conversation.ops_status);
            }
          } else {
            setData(res.data);
          }
        }
      })
      .catch((err) => {
        if (mounted) {
          setError(err?.response?.data?.detail || err?.message || "Failed to load conversation history.");
        }
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [conversationId]);

  const handleSaveEvaluation = async () => {
    if (!conversationId) return;
    setSavingEval(true);
    setEvalFeedback(null);
    try {
      const res = await api.post(
        `/ai/chat-history/conversations/${conversationId}/evaluation`,
        {
          status: evalStatus,
          remarks: evalRemarks.trim() || null,
        }
      );
      if (res.data?.success) {
        const updated = res.data.evaluation || {
          conversation_id: conversationId,
          status: evalStatus,
          remarks: evalRemarks.trim() || null,
          evaluated_at: new Date().toISOString().replace("T", " ").substring(0, 19),
        };
        setData((prev) =>
          prev ? { ...prev, ops_status: updated.status, evaluation: updated } : prev
        );
        setEvalFeedback({ type: "success", message: "Evaluation saved successfully!" });
        if (onEvaluationUpdated) {
          onEvaluationUpdated(updated);
        }
        setTimeout(() => setEvalFeedback(null), 3500);
      } else {
        setEvalFeedback({
          type: "error",
          message: res.data?.message || "Failed to update evaluation",
        });
      }
    } catch (err) {
      console.error("Evaluation update error:", err);
      setEvalFeedback({
        type: "error",
        message:
          err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to save evaluation",
      });
    } finally {
      setSavingEval(false);
    }
  };

  // Handle ESC key to close modal or lightbox
  useEffect(() => {
    function handleKeyDown(e) {
      if (e.key === "Escape") {
        if (lightboxImg) {
          setLightboxImg(null);
        } else {
          onClose();
        }
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [lightboxImg, onClose]);

  const conv = data || initialData || {};
  const customer = conv.customer;
  const isGuest = conv.user_type === "guest";
  const isActive = conv.status === "active";

  return (
    <>
      {/* Modal Backdrop */}
      <div
        className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-fade-up"
        onClick={(e) => {
          if (e.target === e.currentTarget) onClose();
        }}
        data-testid="conversation-history-modal"
      >
        <div className="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-4xl max-h-[92vh] flex flex-col overflow-hidden">
          {/* Header */}
          <div className="bg-slate-900 text-white px-6 py-4 flex items-center justify-between border-b border-slate-800 shrink-0">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-[#6E3667] flex items-center justify-center text-white shrink-0 shadow-sm">
                <Bot className="w-5 h-5 text-white" />
              </div>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-heading font-bold text-base truncate text-white">
                    Conversation #{conv.id || conversationId}
                  </span>
                  <span
                    className={cn(
                      "text-[11px] font-semibold px-2 py-0.5 rounded-full border",
                      isActive
                        ? "bg-emerald-500/20 text-emerald-300 border-emerald-500/30"
                        : "bg-slate-500/20 text-slate-300 border-slate-500/30"
                    )}
                  >
                    {conv.status ? conv.status.toUpperCase() : "ACTIVE"}
                  </span>
                  <span
                    className={cn(
                      "text-[11px] font-semibold px-2 py-0.5 rounded-full border",
                      isGuest
                        ? "bg-amber-500/20 text-amber-300 border-amber-500/30"
                        : "bg-sky-500/20 text-sky-300 border-sky-500/30"
                    )}
                  >
                    {isGuest ? "GUEST" : "CUSTOMER"}
                  </span>
                  <span
                    className={cn(
                      "text-[11px] font-semibold px-2.5 py-0.5 rounded-full border flex items-center gap-1.5 shadow-2xs",
                      getOpsStatusBadgeClass(conv.ops_status || evalStatus || "Pending")
                    )}
                  >
                    <span
                      className={cn(
                        "w-1.5 h-1.5 rounded-full",
                        getOpsStatusDotColor(conv.ops_status || evalStatus || "Pending")
                      )}
                    />
                    <span>{conv.ops_status || evalStatus || "Pending"}</span>
                  </span>
                </div>
                <p className="text-xs text-slate-300 truncate mt-0.5 max-w-xl">
                  {conv.title || "AI Chat Session"}
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-slate-400 hover:text-white p-1.5 rounded-lg hover:bg-white/10 transition-colors"
              title="Close modal"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* User / Session Meta Bar */}
          <div className="bg-slate-50 border-b border-slate-200 px-6 py-3 flex flex-wrap items-center justify-between gap-4 text-xs shrink-0">
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-slate-600">
              {customer ? (
                <>
                  <div className="flex items-center gap-1.5 font-medium text-slate-800">
                    <User className="w-3.5 h-3.5 text-primary" />
                    <span>{customer.name || `Customer #${customer.cuid}`}</span>
                    {customer.cuid && <span className="text-slate-400 text-[11px]">(ID: #{customer.cuid})</span>}
                  </div>
                  {customer.mobile && (
                    <div className="flex items-center gap-1.5">
                      <Phone className="w-3.5 h-3.5 text-slate-400" />
                      <a href={`tel:${customer.mobile}`} className="hover:underline text-slate-700">
                        {customer.mobile}
                      </a>
                    </div>
                  )}
                  {customer.email && (
                    <div className="flex items-center gap-1.5">
                      <Mail className="w-3.5 h-3.5 text-slate-400" />
                      <span className="truncate max-w-[200px] text-slate-700">{customer.email}</span>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex items-center gap-1.5 text-slate-600">
                  <User className="w-3.5 h-3.5 text-amber-500" />
                  <span className="font-medium text-slate-800">Guest Visitor</span>
                  {conv.guest_session_id && (
                    <span className="text-slate-400 text-[11px] font-mono">
                      (Session: {conv.guest_session_id})
                    </span>
                  )}
                </div>
              )}
            </div>

              <div className="flex items-center gap-3 text-slate-500 text-[11px] flex-wrap">
                {conv.is_new && (
                  <span
                    className={cn(
                      "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border",
                      getUserStatusBadge(conv.is_new).badgeClass
                    )}
                    title={getUserStatusBadge(conv.is_new).description}
                  >
                    <span className={cn("w-1.5 h-1.5 rounded-full", getUserStatusBadge(conv.is_new).dotClass)} />
                    <span>{getUserStatusBadge(conv.is_new).label}</span>
                  </span>
                )}

                {conv.is_converted && (
                  <button
                    type="button"
                    onClick={() => setShowConversionModal(true)}
                    className={cn(
                      "inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-[10px] font-semibold border transition-all cursor-pointer shadow-2xs hover:scale-105",
                      getConvertedBadge(conv.is_converted).badgeClass
                    )}
                    title="Click to view conversion details"
                  >
                    <span className={cn("w-1.5 h-1.5 rounded-full", getConvertedBadge(conv.is_converted).dotClass)} />
                    <span>Converted: {conv.is_converted}</span>
                    <ExternalLink className="w-2.5 h-2.5 opacity-60 ml-0.5" />
                  </button>
                )}

                {conv.created_at && (
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5 text-slate-400" />
                    <span>Started: {conv.created_at}</span>
                  </div>
                )}
                {conv.updated_at && (
                  <div className="flex items-center gap-1">
                    <Clock className="w-3.5 h-3.5 text-slate-400" />
                    <span>Updated: {conv.updated_at}</span>
                  </div>
                )}
                {conv.messages && (
                  <span className="bg-slate-200/80 text-slate-700 font-semibold px-2 py-0.5 rounded">
                    {conv.messages.length} messages
                  </span>
                )}
              </div>
            </div>

            {/* Transcript Scroll Area */}
            <div className="flex-1 overflow-y-auto scrollbar-thin p-6 space-y-4 bg-slate-50/50">
              {loading ? (
                <div className="py-16 flex flex-col items-center justify-center text-slate-400 gap-3">
                  <RefreshCw className="w-6 h-6 animate-spin text-primary" />
                  <span className="text-sm">Loading full conversation transcript...</span>
                </div>
              ) : error ? (
                <div className="py-12 px-4 max-w-md mx-auto text-center space-y-3">
                  <AlertCircle className="w-8 h-8 text-red-500 mx-auto" />
                  <p className="text-sm text-red-600 font-medium">{error}</p>
                  <button
                    onClick={() => {
                      setLoading(true);
                      setError(null);
                      api
                        .get(`/ai/chat-history/conversations/${conversationId}`)
                        .then((res) => setData(res.data?.conversation || res.data))
                        .catch((err) => setError(err?.message || "Failed to reload"))
                        .finally(() => setLoading(false));
                    }}
                    className="px-3 py-1.5 text-xs bg-primary text-white rounded-md hover:bg-primary/90 transition-colors"
                  >
                    Retry Loading
                  </button>
                </div>
              ) : !conv.messages || conv.messages.length === 0 ? (
                <div className="py-16 text-center text-slate-400 text-sm">
                  No message records found in this conversation.
                </div>
              ) : (
                conv.messages.map((msg, idx) => {
                  const isUser = msg.role === "user";
                  return (
                    <div
                      key={msg.id || idx}
                      className={cn("flex gap-3 max-w-[85%]", isUser ? "ml-auto flex-row-reverse" : "mr-auto")}
                    >
                      {/* Avatar */}
                      <div
                        className={cn(
                          "w-8 h-8 rounded-full flex items-center justify-center shrink-0 text-xs shadow-xs",
                          isUser ? "bg-slate-200 text-slate-700" : "bg-[#6E3667] text-white"
                        )}
                        title={isUser ? "User" : "SpinCycles AI Assistant"}
                      >
                        {isUser ? <User className="w-4 h-4" /> : <Bot className="w-4 h-4 text-white" />}
                      </div>

                      {/* Message Bubble */}
                      <div className="space-y-1.5 min-w-0">
                        <div className={cn("flex items-center gap-2 text-[11px] text-slate-400", isUser && "justify-end")}>
                          <span className="font-semibold text-slate-600">
                            {isUser ? (customer?.name || "Customer") : "SpinCycles AI"}
                          </span>
                          {msg.created_at && <span>{msg.created_at}</span>}
                        </div>

                        <div
                          className={cn(
                            isRichEmbed(msg.message) && !isUser
                              ? "w-full"
                              : cn(
                                "rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap break-words shadow-xs border",
                                isUser
                                  ? "bg-[#6E3667] text-white border-transparent rounded-tr-xs"
                                  : "bg-white text-slate-800 border-slate-200 rounded-tl-xs"
                              )
                          )}
                        >
                          <FormattedMessage content={msg.message} isUser={isUser} onImageClick={setLightboxImg} />

                          {/* Attached Image if present */}
                          {msg.image_url && (
                            <div className="mt-3 pt-2 border-t border-black/10">
                              <div className="text-[11px] font-medium opacity-80 mb-1.5 flex items-center gap-1">
                                <ImageIcon className="w-3.5 h-3.5" />
                                <span>Attached Photo:</span>
                              </div>
                              <div
                                onClick={() => setLightboxImg(msg.image_url)}
                                className="relative group cursor-pointer inline-block overflow-hidden rounded-lg border border-slate-200 shadow-sm max-w-xs bg-slate-100"
                              >
                                <img
                                  src={msg.image_url}
                                  alt="User attachment"
                                  referrerPolicy="no-referrer"
                                  className="max-h-48 rounded-lg object-cover transition-transform duration-200 group-hover:scale-105"
                                  loading="lazy"
                                />
                                <div className="absolute inset-0 bg-black/30 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white text-xs font-semibold gap-1">
                                  <Maximize2 className="w-4 h-4" />
                                  <span>Click to enlarge</span>
                                </div>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })
              )}
            </div>

            {/* Agent Evaluation & Review Feedback Footer Bar */}
            <div className="border-t border-slate-200 bg-slate-50/90 p-4 shrink-0 space-y-2.5">
              <div className="flex items-center gap-2 text-xs font-bold text-slate-800">
                <span>📝</span>
                <span>Agent Evaluation & Review Feedback</span>
                {(data?.evaluation?.evaluator_name || data?.evaluation?.evaluator_mail) && (
                  <span className="text-[11px] font-normal text-slate-500 ml-auto">
                    Last evaluated by{" "}
                    <strong className="text-slate-700">
                      {data.evaluation.evaluator_name || data.evaluation.evaluator_mail}
                    </strong>
                    {data.evaluation.evaluated_at && ` on ${data.evaluation.evaluated_at}`}
                  </span>
                )}
              </div>

              <div className="flex flex-wrap sm:flex-nowrap items-center gap-2.5">
                {/* Status Dropdown */}
                <div className="relative min-w-[170px]">
                  <select
                    value={evalStatus}
                    onChange={(e) => setEvalStatus(e.target.value)}
                    className="w-full px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs font-semibold text-slate-800 focus:outline-none focus:ring-1 focus:ring-[#6E3667] focus:border-[#6E3667] cursor-pointer"
                  >
                    <option value="Pending">🟡 Pending</option>
                    <option value="Under review">🔵 Under review</option>
                    <option value="Converted">🟢 Converted</option>
                    <option value="valid response">🟢 valid response</option>
                    <option value="Improve - KB - RD">🟠 Improve - KB - RD</option>
                  </select>
                </div>

                {/* Remark Text Input */}
                <input
                  type="text"
                  value={evalRemarks}
                  onChange={(e) => setEvalRemarks(e.target.value)}
                  placeholder="Enter evaluation notes / action taken (e.g. 'Updated prompt/KB for pickup timing')..."
                  className="flex-1 px-3 py-2 bg-white border border-slate-300 rounded-lg text-xs text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-[#6E3667] focus:border-[#6E3667]"
                />

                {/* Save Button */}
                <button
                  type="button"
                  disabled={savingEval}
                  onClick={handleSaveEvaluation}
                  className="px-4 py-2 bg-[#6E3667] hover:bg-[#592a53] text-white text-xs font-semibold rounded-lg shadow-xs transition-colors shrink-0 disabled:opacity-50 cursor-pointer flex items-center gap-1.5"
                >
                  {savingEval ? (
                    <>
                      <RefreshCw className="w-3.5 h-3.5 animate-spin" />
                      <span>Saving...</span>
                    </>
                  ) : (
                    <span>Save Evaluation</span>
                  )}
                </button>
              </div>

              {/* Visual Feedback Message */}
              {evalFeedback && (
                <div
                  className={cn(
                    "text-xs px-3 py-1.5 rounded-md flex items-center gap-1.5 font-medium animate-fade-in",
                    evalFeedback.type === "success"
                      ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                      : "bg-red-50 text-red-700 border border-red-200"
                  )}
                >
                  {evalFeedback.type === "success" ? (
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                  ) : (
                    <AlertCircle className="w-3.5 h-3.5 text-red-600 shrink-0" />
                  )}
                  <span>{evalFeedback.message}</span>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="bg-white border-t border-slate-200 px-6 py-3 flex items-center justify-between text-xs text-slate-500 shrink-0">
              <div>
                Status: <span className="font-medium text-slate-800 capitalize">{conv.status || "active"}</span>
              </div>
              <button
                onClick={onClose}
                className="px-4 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-medium rounded-md transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>

        {/* Fullscreen Lightbox Modal for Attachment Images */}
        {lightboxImg && (
          <div
            className="fixed inset-0 z-60 bg-black/90 backdrop-blur-md flex items-center justify-center p-4"
            onClick={() => setLightboxImg(null)}
          >
            <div className="relative max-w-4xl max-h-[90vh] flex flex-col items-center">
              <button
                onClick={() => setLightboxImg(null)}
                className="absolute -top-10 right-0 text-white/80 hover:text-white p-1 rounded-full bg-white/10 hover:bg-white/20 transition-colors"
                title="Close image"
              >
                <X className="w-6 h-6" />
              </button>
              <img
                src={lightboxImg}
                alt="Full attachment view"
                referrerPolicy="no-referrer"
                className="max-h-[85vh] max-w-full rounded-lg shadow-2xl object-contain"
                onClick={(e) => e.stopPropagation()}
              />
              <div className="mt-2 flex items-center gap-2">
                <a
                  href={lightboxImg}
                  target="_blank"
                  rel="noreferrer"
                  className="text-xs text-white/80 hover:text-white underline flex items-center gap-1"
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Open original photo in new tab
                </a>
              </div>
            </div>
          </div>
        )}
        {/* Conversion Details Modal */}
        {showConversionModal && (
          <ConversionDetailModal
            conversation={conv}
            onClose={() => setShowConversionModal(false)}
          />
        )}
      </>
      );
}

